/* eslint-disable */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ShieldCheck, Sparkle, ArrowUpRight, Lock, FileText, Flask,
  Package, Megaphone, Database, Plus, CheckCircle, WarningCircle,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { HaleWordmark } from '../components/HaleBrand';
import TI from '../components/brand/TI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IVORY = '#F5F1E8';
const SLATE = '#0B1426';
const CORAL = '#FF4E6A';
const MUTED = '#5B6577';
const RULE  = 'rgba(11,20,38,0.10)';

/* ====================================================================
 * /hale/marketplace  (gated)
 *
 * Premium Hale partner marketplace for approved Hale League Brands.
 * Authorized roles: admin/assessor (ToxIntel), brand users with an
 * active hale_membership OR an approved hale_application.
 *
 * Visual treatment matches the approved ToxIntel landing system:
 *   - ivory cream background, slate body, coral accent
 *   - Cormorant Garamond serif headlines, Inter body
 *   - icon-driven cards, no photography
 *   - category-grouped service tables on desktop
 *   - card-stack rendering on mobile (no horizontal scroll)
 * ==================================================================== */

const CATEGORY_ICONS = {
  core_safety:           Flask,
  regulatory_readiness:  FileText,
  claims_substantiation: Megaphone,
  packaging_rsl:         Package,
  database_access:       Database,
};

const STATUS_BADGES = [
  { label: 'Hale Reviewed™',                tone: 'primary' },
  { label: 'Hale Verified™',                tone: 'muted' },
  { label: 'Pilot Access',                  tone: 'primary' },
  { label: 'Independent Scientific Review', tone: 'muted' },
];

function StatusBadge({ label, tone }) {
  const isPrimary = tone === 'primary';
  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-sm text-[10px] font-mono uppercase tracking-[0.18em]"
      style={{
        color: isPrimary ? CORAL : MUTED,
        backgroundColor: isPrimary ? 'rgba(255,78,106,0.10)' : 'rgba(11,20,38,0.05)',
        border: `1px solid ${isPrimary ? 'rgba(255,78,106,0.30)' : RULE}`,
      }}
    >
      {label}
    </span>
  );
}

function Header() {
  return (
    <header className="border-b" style={{ borderColor: RULE }}>
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3" aria-label="ToxIntel home">
            <span className="text-xl tracking-tight" style={{ color: SLATE, fontFamily: '"Cormorant Garamond", serif' }}>
              <TI color={CORAL} />
            </span>
          </Link>
          <span
            className="ml-3 text-[10px] font-mono uppercase tracking-[0.25em] hidden md:inline px-2 py-1 rounded-sm"
            style={{ backgroundColor: SLATE, color: IVORY }}
          >
            · Hale Marketplace
          </span>
        </div>
        <nav className="flex items-center gap-5">
          <Link to="/hale" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }} data-testid="mkt-nav-overview">
            Hale overview
          </Link>
          <Link to="/hale/dashboard" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }} data-testid="mkt-nav-dashboard">
            Brand dashboard
          </Link>
            <Link to="/dashboard" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: SLATE }} data-testid="mkt-nav-toxintel">
              <TI color={CORAL} />&nbsp;&rarr;
            </Link>
        </nav>
      </div>
    </header>
  );
}

function AccessGate({ reason }) {
  const navigate = useNavigate();
  const isAuthIssue = reason === 'unauthenticated';
  return (
    <div className="min-h-screen" style={{ backgroundColor: IVORY, color: SLATE, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Helmet>
        <title>Hale Marketplace · Access required</title>
      </Helmet>
      <Header />
      <main className="max-w-3xl mx-auto px-6 py-20" data-testid="mkt-access-gate">
        <HaleWordmark size="md" />
        <Lock size={28} weight="duotone" style={{ color: CORAL }} className="mt-6 mb-4" />
        <h1
          className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4"
          style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
          data-testid="mkt-gate-title"
        >
          {isAuthIssue ? 'Sign in to access the Hale Marketplace.' : 'Hale Marketplace is gated.'}
        </h1>
        <p className="text-base leading-relaxed mb-8 max-w-2xl" style={{ color: MUTED }}>
          {isAuthIssue
            ? 'The marketplace is restricted to approved Hale League Brands and Hale / ToxIntel admins. Sign in or apply for access to continue.'
            : 'Your account exists, but it doesn\u2019t currently have an active Hale membership or an approved application. The marketplace pricing, service scope, and partner materials are restricted to approved brands.'}
        </p>
        <div className="flex flex-wrap gap-3 mb-10">
          {isAuthIssue && (
            <button
              onClick={() => navigate('/login?return=/hale/marketplace')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-[13px] font-medium uppercase tracking-wider transition-transform hover:-translate-y-px shadow-sm"
              style={{ backgroundColor: CORAL, color: SLATE }}
              data-testid="mkt-gate-signin"
            >
              Sign in <ArrowUpRight size={14} weight="bold" />
            </button>
          )}
          <Link
            to="/hale/apply"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-[13px] font-medium uppercase tracking-wider border"
            style={{ borderColor: SLATE, color: SLATE }}
            data-testid="mkt-gate-apply"
          >
            Apply for Hale access
          </Link>
          <a
            href="mailto:partnerships@tagc.us?subject=Hale%20Marketplace%20%E2%80%94%20Access%20Request"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-[13px] font-medium uppercase tracking-wider"
            style={{ color: MUTED }}
            data-testid="mkt-gate-contact"
          >
            Contact partnerships &rarr;
          </a>
        </div>
        <p className="text-[11px] font-mono uppercase tracking-[0.25em]" style={{ color: MUTED }}>
          Hale by ToxIntel · regulatory@tagc.us
        </p>
      </main>
    </div>
  );
}

export default function HaleMarketplacePage() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gateReason, setGateReason] = useState(null);
  const [scope, setScope] = useState(() => new Set()); // service ids added to scope (local-only)

  const fetchExhibit = useCallback(async () => {
    setLoading(true);
    setGateReason(null);
    try {
      const r = await axios.get(`${API_URL}/api/hale-marketplace/exhibit-a`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 401) setGateReason('unauthenticated');
      else if (status === 403) setGateReason(detail?.reason || 'no_approved_access');
      else setGateReason('error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    fetchExhibit();
  }, [authLoading, user, fetchExhibit]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: IVORY, color: SLATE, fontFamily: 'Inter, system-ui, sans-serif' }}>
        <Header />
        <main className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-sm font-mono uppercase tracking-[0.2em]" style={{ color: MUTED }} data-testid="mkt-loading">
            Loading marketplace…
          </p>
        </main>
      </div>
    );
  }

  if (gateReason && gateReason !== 'error') {
    return <AccessGate reason={gateReason} />;
  }

  if (!data) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: IVORY, color: SLATE, fontFamily: 'Inter, system-ui, sans-serif' }}>
        <Header />
        <main className="max-w-3xl mx-auto px-6 py-20" data-testid="mkt-error">
          <WarningCircle size={24} weight="fill" style={{ color: '#B0312D' }} />
          <h1 className="mt-4 text-2xl font-extrabold tracking-tight" style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>
            Could not load the marketplace.
          </h1>
          <p className="mt-3 text-sm" style={{ color: MUTED }}>
            Please try again or contact <a href="mailto:regulatory@tagc.us" className="underline">regulatory@tagc.us</a>.
          </p>
        </main>
      </div>
    );
  }

  const servicesByCategory = data.services.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});

  const toggleScope = (id) => {
    setScope(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const requestReviewHref = (s) =>
    `mailto:regulatory@tagc.us?subject=Hale%20Marketplace%20%E2%80%94%20Request%20Review%3A%20${encodeURIComponent(s.name)}&body=${encodeURIComponent('Please open a Hale ' + s.name + ' engagement. Brand: ' + (user?.email || '[email]') + '. Notes:\n')}`;

  return (
    <div className="min-h-screen" style={{ backgroundColor: IVORY, color: SLATE, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Helmet>
        <title>Hale Marketplace · ToxIntel</title>
      </Helmet>
      <Header />

      <main>
        {/* HERO */}
        <section className="border-b" style={{ borderColor: RULE }}>
          <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2">
                <HaleWordmark size="md" />
                <p className="text-[10px] font-mono uppercase tracking-[0.35em] mt-5 mb-3" style={{ color: CORAL }}>
                  Hale Marketplace · approved-brand catalog
                </p>
                <h1
                  className="text-[clamp(2.25rem,4.4vw,3.75rem)] font-extrabold tracking-tight leading-[1.02] mb-6"
                  style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
                  data-testid="mkt-hero-title"
                >
                  Hale Marketplace.
                </h1>
                <p className="text-base sm:text-lg leading-relaxed max-w-2xl mb-6" style={{ color: MUTED }}>
                  Curated scientific, regulatory, packaging, and claims review services for the
                  Hale Reviewed™ and Hale Verified™ brand pathways. Powered by ToxIntel scientific review
                  and governance infrastructure.
                </p>
                <div className="flex flex-wrap gap-2" data-testid="mkt-status-badges">
                  {STATUS_BADGES.map(b => <StatusBadge key={b.label} {...b} />)}
                </div>
              </div>

              {/* Access summary card */}
              <aside className="lg:col-span-1">
                <div
                  className="rounded-sm border p-6 h-full"
                  style={{ borderColor: CORAL, backgroundColor: 'rgba(255,255,255,0.6)' }}
                  data-testid="mkt-access-summary"
                >
                  <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-3" style={{ color: CORAL }}>
                    Your access
                  </p>
                  <p className="text-2xl font-extrabold tracking-tight mb-2"
                     style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>
                    {data.access?.role === 'admin' ? 'Admin · Full catalog'
                      : data.access?.role === 'brand_member' ? 'Approved · Active member'
                      : data.access?.role === 'brand_approved' ? 'Approved · Activation pending'
                      : 'Approved'}
                  </p>
                  <p className="text-[12px] mb-4" style={{ color: MUTED }}>
                    Signed in as <span className="font-semibold" style={{ color: SLATE }}>{user?.email || '—'}</span>
                  </p>
                  <Link
                    to="/hale/dashboard"
                    className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider"
                    style={{ color: CORAL }}
                    data-testid="mkt-access-to-dashboard"
                  >
                    Open brand dashboard <ArrowUpRight size={11} weight="bold" />
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* CATEGORY SUMMARY CARDS */}
        <section className="border-b" style={{ borderColor: RULE }}>
          <div className="max-w-7xl mx-auto px-6 py-14">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-3" style={{ color: CORAL }}>
              Service categories
            </p>
            <h2
              className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-8 max-w-3xl"
              style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
            >
              Four review domains plus enterprise intelligence access.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="mkt-category-cards">
              {data.categories.filter(c => c.id !== 'database_access').map(cat => {
                const Icon = CATEGORY_ICONS[cat.id] || Sparkle;
                const count = servicesByCategory[cat.id]?.length || 0;
                return (
                  <a
                    key={cat.id}
                    href={`#cat-${cat.id}`}
                    className="rounded-sm border p-5 transition-transform hover:-translate-y-px hover:shadow-sm block"
                    style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }}
                    data-testid={`mkt-category-card-${cat.id}`}
                  >
                    <Icon size={22} weight="duotone" style={{ color: CORAL }} className="mb-3" />
                    <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-1" style={{ color: MUTED }}>
                      {String(count).padStart(2, '0')} services
                    </p>
                    <p className="text-base font-semibold mb-2" style={{ color: SLATE }}>{cat.label}</p>
                    <p className="text-[12px] leading-relaxed" style={{ color: MUTED }}>{cat.desc}</p>
                  </a>
                );
              })}
            </div>
          </div>
        </section>

        {/* CATEGORIZED SERVICES */}
        {data.categories.map(cat => {
          const rows = servicesByCategory[cat.id] || [];
          if (rows.length === 0) return null;
          const Icon = CATEGORY_ICONS[cat.id] || Sparkle;
          const isDatabase = cat.id === 'database_access';
          return (
            <section
              key={cat.id}
              id={`cat-${cat.id}`}
              className="border-b scroll-mt-20"
              style={{ borderColor: RULE, backgroundColor: isDatabase ? 'rgba(11,20,38,0.025)' : 'transparent' }}
              data-testid={`mkt-category-section-${cat.id}`}
            >
              <div className="max-w-7xl mx-auto px-6 py-14">
                <div className="flex items-start gap-4 mb-3">
                  <Icon size={26} weight="duotone" style={{ color: CORAL }} />
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>
                      {String(rows.length).padStart(2, '0')} {rows.length === 1 ? 'service' : 'services'}
                    </p>
                    <h2
                      className="text-2xl sm:text-3xl font-extrabold tracking-tight"
                      style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
                    >
                      {cat.label}
                    </h2>
                  </div>
                </div>
                <p className="text-sm mb-8 max-w-3xl" style={{ color: MUTED }}>{cat.desc}</p>

                {/* DESKTOP table */}
                <div className="hidden lg:block" data-testid={`mkt-table-${cat.id}`}>
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="text-left border-b" style={{ color: MUTED, borderColor: RULE }}>
                        <th className="py-3 pr-4 font-mono uppercase tracking-wider text-[10px] w-1/3">Service</th>
                        <th className="py-3 px-4 font-mono uppercase tracking-wider text-[10px]">Unit</th>
                        <th className="py-3 px-4 font-mono uppercase tracking-wider text-[10px]">Rate</th>
                        <th className="py-3 px-4 font-mono uppercase tracking-wider text-[10px]">Includes</th>
                        <th className="py-3 pl-4 font-mono uppercase tracking-wider text-[10px] text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(s => {
                        const inScope = scope.has(s.id);
                        return (
                          <tr key={s.id} className="border-b align-top" style={{ borderColor: RULE }} data-testid={`mkt-row-${s.id}`}>
                            <td className="py-5 pr-4">
                              <p className="font-semibold mb-1" style={{ color: SLATE }}>{s.name}</p>
                              <p className="text-[10px] font-mono uppercase tracking-wider" style={{ color: MUTED }}>
                                {s.track === 'hale_reviewed' ? 'Reviewed™' : 'Verified™'}
                              </p>
                            </td>
                            <td className="py-5 px-4 text-[12px] font-mono uppercase tracking-wider" style={{ color: MUTED }}>
                              {s.unit}
                            </td>
                            <td className="py-5 px-4 tabular-nums font-semibold whitespace-nowrap" style={{ color: SLATE }}>
                              {s.rate_display}
                            </td>
                            <td className="py-5 px-4">
                              <ul className="space-y-1 text-[12px] leading-snug" style={{ color: MUTED }}>
                                {s.includes.map(item => (
                                  <li key={item} className="flex items-start gap-1.5">
                                    <span style={{ color: CORAL }}>·</span>
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            </td>
                            <td className="py-5 pl-4 text-right whitespace-nowrap">
                              <div className="inline-flex flex-col gap-2 items-end">
                                <a
                                  href={requestReviewHref(s)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-medium uppercase tracking-wider transition-transform hover:-translate-y-px"
                                  style={{ backgroundColor: CORAL, color: SLATE }}
                                  data-testid={`mkt-request-${s.id}`}
                                >
                                  Request review <ArrowUpRight size={11} weight="bold" />
                                </a>
                                <button
                                  onClick={() => toggleScope(s.id)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-medium uppercase tracking-wider border"
                                  style={{
                                    borderColor: inScope ? CORAL : RULE,
                                    color: inScope ? CORAL : SLATE,
                                    backgroundColor: inScope ? 'rgba(255,78,106,0.05)' : 'transparent',
                                  }}
                                  data-testid={`mkt-scope-${s.id}`}
                                >
                                  {inScope ? <><CheckCircle size={11} weight="fill" /> In scope</> : <><Plus size={11} weight="bold" /> Add to scope</>}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* MOBILE / TABLET cards */}
                <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`mkt-cards-${cat.id}`}>
                  {rows.map(s => {
                    const inScope = scope.has(s.id);
                    return (
                      <div
                        key={s.id}
                        className="rounded-sm border p-5"
                        style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.7)' }}
                        data-testid={`mkt-card-${s.id}`}
                      >
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div>
                            <p className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: MUTED }}>
                              {s.track === 'hale_reviewed' ? 'Reviewed™' : 'Verified™'} · {s.unit}
                            </p>
                            <p className="font-semibold leading-tight" style={{ color: SLATE }}>{s.name}</p>
                          </div>
                          <p className="text-sm tabular-nums font-semibold whitespace-nowrap text-right" style={{ color: SLATE }}>
                            {s.rate_display}
                          </p>
                        </div>
                        <ul className="space-y-1 text-[12px] mb-4" style={{ color: MUTED }}>
                          {s.includes.map(item => (
                            <li key={item} className="flex items-start gap-1.5">
                              <span style={{ color: CORAL }}>·</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                        <div className="flex gap-2 flex-wrap">
                          <a
                            href={requestReviewHref(s)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-medium uppercase tracking-wider flex-1 justify-center"
                            style={{ backgroundColor: CORAL, color: SLATE }}
                          >
                            Request review <ArrowUpRight size={11} weight="bold" />
                          </a>
                          <button
                            onClick={() => toggleScope(s.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-medium uppercase tracking-wider border flex-1 justify-center"
                            style={{
                              borderColor: inScope ? CORAL : RULE,
                              color: inScope ? CORAL : SLATE,
                            }}
                          >
                            {inScope ? <><CheckCircle size={11} weight="fill" /> In scope</> : <><Plus size={11} weight="bold" /> Add</>}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          );
        })}

        {/* Notes + Scope Summary */}
        <section className="border-b" style={{ borderColor: RULE, backgroundColor: 'rgba(11,20,38,0.025)' }}>
          <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Notes (2/3 wide on desktop) */}
            <div
              className="lg:col-span-2 rounded-sm border p-6"
              style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.7)' }}
              data-testid="mkt-notes"
            >
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-3" style={{ color: CORAL }}>
                Notes · payment terms · referral structure
              </p>
              <ul className="space-y-2 text-[13px]">
                {data.notes.map((n, idx) => (
                  <li key={idx} className="flex items-start gap-2" style={{ color: SLATE }}>
                    <span style={{ color: CORAL }} className="font-bold flex-shrink-0">·</span>
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Scope summary (1/3) */}
            <div
              className="rounded-sm border-2 p-6"
              style={{ borderColor: CORAL, backgroundColor: IVORY }}
              data-testid="mkt-scope-summary"
            >
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-3" style={{ color: CORAL }}>
                Your scope · this session
              </p>
              <p className="text-4xl font-extrabold tabular-nums mb-2"
                 style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
                 data-testid="mkt-scope-count">
                {scope.size}
              </p>
              <p className="text-sm mb-4" style={{ color: MUTED }}>
                services added to your engagement scope
              </p>
              {scope.size > 0 && (
                <a
                  href={`mailto:regulatory@tagc.us?subject=Hale%20Marketplace%20%E2%80%94%20Scope%20Request%20(${scope.size}%20services)&body=${encodeURIComponent('Please open an engagement covering the following Hale services:\n\n' + Array.from(scope).map(id => '- ' + (data.services.find(s => s.id === id)?.name || id)).join('\n') + '\n\nBrand contact: ' + (user?.email || '[email]'))}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-[12px] font-medium uppercase tracking-wider"
                  style={{ backgroundColor: CORAL, color: SLATE }}
                  data-testid="mkt-scope-submit"
                >
                  Submit scope request <ArrowUpRight size={12} weight="bold" />
                </a>
              )}
            </div>
          </div>
        </section>

        {/* FOOTER — ToxIntel-led; no Aᴳ on Hale public surface */}
        <footer className="border-t" style={{ borderColor: RULE }}>
          <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-base tracking-tight" style={{ color: SLATE, fontFamily: '"Cormorant Garamond", serif' }}>
                <TI color={CORAL} />
              </span>
              <span className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: MUTED }}>
                × Hale Marketplace
              </span>
            </div>
            <p className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: MUTED }}>
              ToxIntel · Hale Partnership · regulatory@tagc.us
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}
